import { AxiosResponse } from 'axios';
import { EditableJob } from '@/services/jobs';
import { User, WorkingTime } from './api';

export type APIResponseType<T> = Promise<AxiosResponse<T>>;

export enum LocalStorageItemKeys {
  USER_TOKEN = 'user:token',
  TOKEN_DATE = 'user:dateToken',
  USER_DATA = 'user:data',
  SIDEBAR_STATUS = 'sidebar:status',
  FILTERS = 'candidate:filters',
  CANDIDATE_TABLE_PAGINATION = 'candidate:pagination',
  JOBS_TABLE_PAGINATION = 'jobs:pagination',
  RECENT_SERVICES = 'services:recent',
}

export enum HTTPMethods {
  POST = 'post',
  GET = 'get',
  PUT = 'put',
  DELETE = 'delete',
}

export enum Placeholders {
  NOT_SUBMITTED_YET = '---',
  NO_FILTERED_DATA = 'There is no data for these filtered results',
}

export type CustomColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success'
  | 'auxRed'
  | 'auxGreen';

export type VariantColor = 'main' | 'light' | 'dark' | 'contrastText';

export type TableColumnOrder = 'asc' | 'desc';

export type HeaderTable<CustomType> = {
  id: keyof CustomType;
  label: string;
} | null;

export type Auth0User = {
  sub?: string;
  given_name?: string;
  family_name?: string;
  nickname?: string;
  name?: string;
  picture?: string;
  locale?: string;
  updated_at?: string;
  email?: string;
  email_verified?: boolean;
};

export type SystemUser = User &
  Auth0User & {
    username?: string;
    initials?: string;
    isNotInvited?: boolean;
    needsOnboarding?: boolean;
    isLoading?: boolean;
  };

export enum FormErrors {
  MISSING_REQUIRED_FIELDS = 'Please, fill out all required questions.',
  GENERIC = 'There was an error, please, try to submit later.',
}

export enum UnreviewedCheckinsTabsKeys {
  TEAM = 'Teams',
  EVERYONE = 'Everyone',
}

export enum FetchApiIds {
  CANDIDATE_BY_ID = 'candidate-by-id',
  ALL_REVIEWERS = 'all-reviewers',
  TEAM_INTERVIEW_REVIEWERS = 'team-interview-reviewers',
  TECHNICAL_CHALLENGE_REVIEWERS = 'technical-challenge-reviewers',
  TECHNICAL_CHALLENGE_SUGGESTIONS = 'technical-challenge-suggestions',
  PUBLIC_CANDIDATE_INTERVIEW = 'public-candidate-interview',
  CANDIDATE_INTERVIEW = 'candidate-interviews',
  CANDIDATE_PROJECTS = 'candidate-projects',
  ALL_TECHS = 'all-techs',
  TECH_STACKS = 'tech-stacks',
  ALL_SEMINARS = 'all-seminars',
  SEMINAR_BY_ID = 'seminar-by-id',
  USERS = 'users',
  ALL_CHECKINS = 'get-all-checkins',
  USERS_CHECKINS_SUMMARY = 'users-checkins-summary',
  USER_CHECKINS_SUMMARY = 'user-checkins-summary',
  ROTATING_QUESTIONS = 'rotating-questions',
  CHECKIN_BY_ID = 'checkin-by-id',
  CHECKIN_BY_ID_FOR_ADMIN = 'checkin-by-id-for-admin',
  PROFILE = 'profile',
  TRENDS = 'trends',
  TRENDS_DETAIL = 'trends-detail',
  AVERAGE_PULSES = 'average-pulses',
  CHECKIN_REPORTS = 'checkin-reports',
  UNREVIEWED_CHECKINS = 'unreviewed-checkins',
  UNREVIEWED_CHECKINS_BY_TEAM = 'unreviewed-checkins-by-team',
  GET_ALL_TEAMS = 'get-all-teams',
  GET_TEAM_MEMBERS = 'get-team-members',
  GET_ALL_UERS = 'get-all-users',
  GET_ALL_PENDING_CHECKINS = 'get-all-pending-checkins',
  CANDIDATE_DETAILS = 'candidate-details',
  CANDIDATE_PROFILE = 'candidate-profile',
  GET_ALL_ATTACHMENTS = 'get-all-attachments',
  GET_MY_ATTACHMENTS = 'get-my-attachments',
  GET_NEW_CANDIDATES = 'get-new-candidates',
  GET_TOTAL_CANDIDATES_BY_TECH_STACK = 'get-total-candidates-by-tech-stack',
  GET_AVG_TIME_TO_ACCEPTANCE_OR_REJECTION_GROUPED_BY_TECH_STACK = 'get-avg-time-to-acceptance-or-rejection-grouped-by-tech-stack',
  AVG_TIME_BY_OWNER = 'avg-time-by-owner',
  CANDIDATES_LIST = 'candidates-list',
  JOBS_LIST = 'jobs-list',
  JOBS_DETAILS = 'jobs-details',
  JOBS_TOPICS = 'jobs-topics',
  CANDIDATES_FILTERED = 'filtered',
  CANDIDATE_SEARCH = 'candidate-search',
  JOB_SEARCH = 'job-search',
  VALIDATE_ACCESS = 'users-privileges-access',
  CANDIDATE_STAGES = 'candidate-stages',
  CANDIDATES_BOARD = 'candidates-board',
  HIRING_REPORTS = 'hiring-reports',
  HIRING_TRENDS = 'hiring-trends',
  NEW_CANDIDATES_BY_OWNER = 'new-candidates-by-owner',
  ORIGIN_CHART = 'grouped-by-status',
  REJECTED_CHART = 'grouped-by-rejected_status',
  AVG_TIME_IN_DAYS_CHART = 'avg-time-in-days-chart',
  READY_TO_HIRE = 'ready-to-hire',
  GET_CANDIDATE_STATUSES = 'get-candidate-statuses',
  TECHNICAL_CHALLENGES = 'get-technical-challenges',
  INTERVIEW_TECHNICAL_CHALLENGES = 'get-interview-technical-challenges',
  PARTNER_ACCOUNTS = 'get-partner-accounts',
  BENEFITS_AND_PERKS = 'benefits-and-perks',
  GET_STAFF_MEMBER = 'get-staff-member',
  GET_STAFF_MEMBER_BY_PERSON = 'get-staff-member-by-person',
  GET_JOB_CANDIDATES_BY_STAGE = 'get-job-candidates-by-stage',
  GET_TEAM_STAFF_MEMBERS = 'get-team-staff-members',
  GET_MY_REVIEWS = 'get-my-reviews',
  GET_PTOS_BY_STAFF_MEMBER = 'get-ptos-by-staff-member',
  GET_BANK_OF_HOURS_BALANCE = 'get-bank-of-hours-balance',
  GET_PTOS_AS_ADMIN = 'get-ptos-as-admin',
  GET_OVERLAPPING_PTOS_BY_TEAM = 'get-overlapping-ptos-by-team',
  GET_COMPENSATIONS = 'get-compensations',
  LIST_STAFF_MEMBERS = 'get-staff-members',
  LIST_JOB_INFOS = 'get-job-infos',
  GET_TEAM = 'get-team-by-id',
  LIST_CELEBRATIONS = 'get-celebrations',
  GET_JOBS_BY_CANDIDATE_ID = 'get-jobs-by-candidate-id',
  SEO_PROJECTS = 'seo-projects',
  SEO_PROJECT_BY_ID = 'seo-project-by-id',
  SEO_AUDITS = 'seo-audits',
  SEO_AUDIT_BY_ID = 'seo-audit-by-id',
  SEO_AUDIT_ISSUES = 'seo-audit-issues',
  SEO_AUDIT_PAGES = 'seo-audit-pages',
  SEO_KEYWORDS = 'seo-keywords',
  SEO_KEYWORD_BY_ID = 'seo-keyword-by-id',
  SEO_KEYWORD_SNAPSHOTS = 'seo-keyword-snapshots',
  SEO_CONTENT_ANALYSES = 'seo-content-analyses',
  SEO_CONTENT_ANALYSIS_BY_ID = 'seo-content-analysis-by-id',
  SEO_GOOGLE_CONNECTION = 'seo-google-connection',
  SEO_SEARCH_CONSOLE_SUMMARY = 'seo-search-console-summary',
  SEO_OPPORTUNITIES = 'seo-opportunities',
  SEO_SERP_ANALYSIS = 'seo-serp-analysis',
  SEO_CONTENT_OPTIMIZATION = 'seo-content-optimization',
  SEO_INTERNAL_LINK_SUGGESTIONS = 'seo-internal-link-suggestions',
  FX_CATEGORIES = 'fx-categories',
  FX_VENDORS = 'fx-vendors',
  FX_VENDOR_BY_ID = 'fx-vendor-by-id',
  FX_SUBSCRIPTIONS = 'fx-subscriptions',
  FX_SUBSCRIPTION_BY_ID = 'fx-subscription-by-id',
  FX_TRANSACTIONS = 'fx-transactions',
  FX_TRANSACTION_BY_ID = 'fx-transaction-by-id',
  FX_TRANSACTION_IMPORTS = 'fx-transaction-imports',
  FX_CATEGORIZATION_RULES = 'fx-categorization-rules',
  FX_DASHBOARD_SUMMARY = 'fx-dashboard-summary',
  FX_SPEND_BY_CATEGORY = 'fx-spend-by-category',
  FX_SPEND_OVER_TIME = 'fx-spend-over-time',
  FX_BANK_CONNECTIONS = 'fx-bank-connections',
  FX_QB_CONNECTIONS = 'fx-qb-connections',
  FX_REVIEW_QUEUE = 'fx-review-queue',
  FX_INVOICES = 'fx-invoices',
  FX_CATEGORIZATION_EVENTS = 'fx-categorization-events',
  FX_BULK_PROCESSORS = 'fx-bulk-processors',
  FX_BULK_REPORTS = 'fx-bulk-reports',
  FX_BULK_REPORT_ITEMS = 'fx-bulk-report-items',
  GITHUB_CONNECTION = 'github-connection',
}

export enum CheckinButtonLabel {
  NOT_REVIEWED = 'Review Check-in',
  REVIEWED = 'View Check-in',
  SUBMITED = 'View Check-in',
  PENDING_FOR_ADMIN = 'Not submitted yet',
  PENDING_FOR_USER = 'Answer Check-in',
}

export enum AppModules {
  HOME = 'home',
  TALENTX = 'talentx',
  PEOPLEX = 'peoplex',
  TRIO_ACADEMY = 'academy',
  SEOX = 'seox',
  /** @deprecated Use SEOX instead */
  GROWTHX = 'seox',
  FINANCEX = 'financex',
  CEOX = 'ceox',
  SETTINGS = 'settings',
  /** @deprecated Use TALENTX instead */
  HIRING = 'hiring',
  /** @deprecated Use PEOPLEX instead */
  PEOPLE = 'people',
}

export enum QuestionType {
  text = 'text',
  dropdown = 'dropdown',
  rating = 'rating',
}

export type OptionsWithLabel<Option = string> = {
  options: Option[];
  label: string;
};

export type ValuesWithLabel<T = string | number> = {
  value: T;
  label: string;
};

export type Question = {
  isDisabled: boolean;
  isMultiple?: boolean;
  name: string;
  label: string;
  type: QuestionType;
  value: string | string[];
  options?: string[];
  optionsWithLabel?: ValuesWithLabel<string>[];
  dropdownGroups?: OptionsWithLabel[];
};

export type Pagination = {
  count: number;
  currentPage: number;
  lastPage: number;
  nextPage: number | null;
  prevPage: number | null;
};

export type ResponseWithPagination<DataType> = Pagination & {
  data: DataType;
};

export type ComponentWithChildrenType = {
  children: JSX.Element;
};

export type SidebarStatusType = {
  isSidebarOpen: boolean;
  toggleSidebarStatus: () => void;
};

export type TechStackType = {
  name: string;
  id: string;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CandidateTechStacksType = {
  id: string;
  skill: TechStackType;
  isMainStack: boolean;
  createdAt: string;
  updatedAt: string;
  description?: string;
  experience?: string;
  experienceInMonths?: number;
  personSkillTags?: any[];
  tags?: any[];
  willingToReview?: boolean;
};

export enum AvailableRoles {
  ACADEMY_ADMIN = 'ACADEMY.ADMIN',
  ACADEMY_USER = 'ACADEMY.USER',
  FREELANCE_REVIEWER = 'HIRING.FREELANCE_REVIEWER',
  FREELANCE_SOURCER = 'HIRING.FREELANCE_SOURCER',
  HIRING_ADMIN = 'HIRING.ADMIN',
  HIRING_USER = 'HIRING.USER',
  INSIDE_REVIEWER = 'HIRING.INSIDE_REVIEWER',
  INSIDE_SOURCER = 'HIRING.INSIDE_SOURCER',
  PEOPLE_ADMIN = 'PEOPLE.ADMIN',
  PEOPLE_USER = 'PEOPLE.USER',
  SEO_ADMIN = 'SEO.ADMIN',
  SEO_USER = 'SEO.USER',
  FINANCE_ADMIN = 'FINANCE.ADMIN',
  FINANCE_USER = 'FINANCE.USER',
}

export const formattedRoles: { [role in AvailableRoles]: string } = {
  [AvailableRoles.ACADEMY_ADMIN]: 'Admin',
  [AvailableRoles.ACADEMY_USER]: 'User',
  [AvailableRoles.FREELANCE_REVIEWER]: 'External Reviewer',
  [AvailableRoles.FREELANCE_SOURCER]: 'External Sourcer',
  [AvailableRoles.HIRING_ADMIN]: 'Admin',
  [AvailableRoles.HIRING_USER]: 'User',
  [AvailableRoles.INSIDE_REVIEWER]: 'Internal Reviewer',
  [AvailableRoles.INSIDE_SOURCER]: 'Internal Sourcer',
  [AvailableRoles.PEOPLE_ADMIN]: 'Admin',
  [AvailableRoles.PEOPLE_USER]: 'User',
  [AvailableRoles.SEO_ADMIN]: 'Admin',
  [AvailableRoles.SEO_USER]: 'User',
  [AvailableRoles.FINANCE_ADMIN]: 'Admin',
  [AvailableRoles.FINANCE_USER]: 'User',
};

export type ParsedAllStatusResponseType = {
  label: string;
  options: string[];
  color: string;
};

interface SkillType {
  id: string;
  name: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ExpertiseType {
  id: string;
  experience: string;
  description: string;
  skill: SkillType;
  subSkills: Array<{
    createdAt: string;
    id: string;
    skill: SkillType;
    updatedAt: string;
  }>;
  tags: TechStackType[];
  personSkillTags: string[];
  isMainStack: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export enum CandidateInterviewPageStages {
  'WELCOME' = 'WELCOME',
  'SUBMIT' = 'SUBMIT',
  'ENDING' = 'ENDING',
}

export type EditableJobProps = {
  workingTimes: WorkingTime[];
  updatedField: string | undefined;
  handleChangeWorkingTimes: (event: any, index: number, field: 'start' | 'end') => void;
  onAddNewWorkingTime: () => void;
  onSubmit: (job: EditableJob) => Promise<void>;
  setUpdatedField: (field: string) => void;
};
